import { useState, useEffect } from 'react';
import { CONFIG, HelpTip, Loading, PANEL_HELP, PanelHowTo, api, useToast } from './_barrel.js';


        function BusinessSignoffPanel({ token, businessId, score, onSigned }) {
            const [assessment, setAssessment] = useState(undefined); // undefined=loading, null=none
            const [summary, setSummary] = useState('');
            const [recs, setRecs] = useState('');
            const [editing, setEditing] = useState(false);
            const [saving, setSaving] = useState(false);
            const [previewing, setPreviewing] = useState(false);
            const [err, setErr] = useState(null);
            const toast = useToast();

            const load = async () => {
                try { setAssessment(await api(`/api/v1/businesses/${businessId}/assessment`, {}, token)); }
                catch (e) { setErr(e.message); setAssessment(null); }
            };

            const preview = async (mode = 'briefing') => {
                setPreviewing(true); setErr(null);
                try {
                    const resp = await fetch(`${CONFIG.apiBase}/api/v1/businesses/${businessId}/report/preview`, {
                        method: 'POST',
                        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                        body: JSON.stringify({ mode, assessment_summary: summary, recommendations: recs || null }),
                    });
                    if (!resp.ok) { const t = await resp.text(); throw new Error(`Preview failed: ${resp.status} ${t.slice(0, 180)}`); }
                    const blob = await resp.blob();
                    const url = URL.createObjectURL(blob);
                    window.open(url, '_blank');
                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                } catch (e) { setErr(e.message); } finally { setPreviewing(false); }
            };
            useEffect(() => { load(); }, [token, businessId]);

            const startEdit = () => {
                setSummary((assessment && assessment.assessment_summary) || (score && score.summary_draft) || '');
                setRecs((assessment && assessment.recommendations) || '');
                setEditing(true);
            };

            const signOff = async () => {
                if (!summary.trim()) { setErr('Assessment summary is required'); return; }
                setSaving(true); setErr(null);
                try {
                    const body = { assessment_summary: summary.trim(), recommendations: recs.trim() || null };
                    if (score) {
                        body.composite_score = Number(score.composite_score);
                        body.risk_band = score.risk_band;
                        body.threat_score = Number(score.threat_score);
                        body.vulnerability_score = Number(score.vulnerability_score);
                        body.impact_score = Number(score.impact_score);
                        body.disinformation_score = Number(score.disinformation_score);
                    }
                    await api(`/api/v1/businesses/${businessId}/assessment`, { method: 'POST', body: JSON.stringify(body) }, token);
                    toast('Assessment signed off — report is no longer a draft'); setEditing(false); await load(); onSigned && onSigned();
                } catch (e) { setErr(e.message); } finally { setSaving(false); }
            };

            return (
              <div className="panel mb-6">
                <div className="panel-header flex justify-between items-center">
                  <div className="font-display text-xl gold">Analyst Assessment &amp; Sign-Off<HelpTip text={PANEL_HELP.analyst_assessment} /></div>
                  {assessment ? <div className="text-xs font-mono" style={{ color: 'var(--green)' }}>● SIGNED</div>
                              : <div className="text-xs font-mono text-muted">Not signed — report downloads as DRAFT</div>}
                </div>
                <PanelHowTo id="analyst_assessment" />
                <div className="px-6 py-5">
                  {err && <div className="text-[var(--red)] text-sm mb-3">{err}</div>}
                  {assessment === undefined ? <div className="text-muted text-sm">Loading…</div> : (
                    editing ? (
                      <div className="space-y-3">
                        <div>
                          <div className="text-xs text-muted uppercase tracking-widest mb-1">Assessment Summary</div>
                          <textarea value={summary} onChange={e => setSummary(e.target.value)} rows={5} style={{ width: '100%' }} placeholder="Your assessment of this company — are they sound, who runs it, the key risks…" />
                        </div>
                        <div>
                          <div className="text-xs text-muted uppercase tracking-widest mb-1">Recommendations (optional)</div>
                          <textarea value={recs} onChange={e => setRecs(e.target.value)} rows={3} style={{ width: '100%' }} placeholder="What the client should do or verify before engaging…" />
                        </div>
                        <div className="flex gap-3">
                          <button className="btn-ghost" onClick={() => preview('briefing')} disabled={previewing} title="See the report with this assessment before signing — nothing is saved">
                            {previewing ? 'Rendering…' : '👁 Preview report'}
                          </button>
                          <button className="btn-gold" onClick={signOff} disabled={saving}>{saving ? 'Signing…' : '✓ Sign Off'}</button>
                          <button className="btn-ghost" onClick={() => setEditing(false)}>Cancel</button>
                        </div>
                        <div className="text-xs text-dim font-mono">Preview opens the report with this assessment (marked DRAFT, nothing saved) so you can review before signing. Signing puts your name on it and removes the DRAFT marker. High/critical bands require lead analyst or admin.</div>
                      </div>
                    ) : assessment ? (
                      <div>
                        <div className="text-xs font-mono text-muted mb-3">Signed by <span className="text-paper">{assessment.signed_by_display_name}</span>{assessment.signed_by_role && ` (${assessment.signed_by_role})`} on {new Date(assessment.signed_at).toLocaleString('en-GB')}</div>
                        <div className="text-sm whitespace-pre-line text-paper mb-3">{assessment.assessment_summary}</div>
                        {assessment.recommendations && <div className="mt-2"><div className="text-xs uppercase tracking-widest text-muted mb-1">Recommendations</div><div className="text-sm whitespace-pre-line text-muted">{assessment.recommendations}</div></div>}
                        <button className="btn-ghost mt-4" onClick={startEdit}>Re-assess &amp; re-sign</button>
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm text-muted mb-4">No signed assessment yet. The report downloads as a DRAFT until an analyst signs off.</div>
                        <button className="btn-gold" onClick={startEdit}>Write assessment &amp; sign off</button>
                      </div>
                    )
                  )}
                </div>
              </div>
            );
        }

export { BusinessSignoffPanel };
